import React from 'react';
import axios from 'axios';
import ReactExport from 'react-data-export';
import ReportExcelFile from './ReportExcelFile';
import { Button, Form, Grid, Header, Icon, Loader, Popup, Segment, Table } from 'semantic-ui-react';
import PaginationFront from '../../util/PaginationFront';
import { additionalFilterSectionStyle, filterFieldGridStyle, requiredFilterSectionStyle } from './reportUiHelpers';
import './AnalyticalReport.css';

const ExcelFile = ReportExcelFile;
const { ExcelSheet, ExcelColumn } = ReactExport.ExcelFile;

const organizationalUnitReports = ['academic-staff-by-title-and-status', 'academic-staff-by-gender-and-age',
    'academic-staff-student-ratio', 'student-mobility', 'students-by-gender', 'graduated-students',
    'teaching-load-by-employee', 'teaching-load-by-type-and-status'];
const studyProgramReports = ['academic-staff-by-gender-and-age', 'academic-staff-student-ratio',
    'students-by-gender', 'graduated-students', 'teaching-load-by-employee',
    'teaching-load-by-type-and-status'];

const reportDefinitions = {
    'academic-staff-by-title-and-status': {
        title: 'Pregled akademskog osoblja prema zvanju i statusu u godini vrednovanja',
        columns: [
            ['academicYear', 'Akademska godina'], ['total', 'Ukupno'],
            ['fullProfessorInternal', 'Stalno zaposleni'], ['fullProfessorExternal', 'Vanjski saradnici'],
            ['associateProfessorInternal', 'Stalno zaposleni'], ['associateProfessorExternal', 'Vanjski saradnici'],
            ['assistantProfessorInternal', 'Stalno zaposleni'], ['assistantProfessorExternal', 'Vanjski saradnici'],
            ['seniorAssistantPhdInternal', 'Stalno zaposleni'], ['seniorAssistantPhdExternal', 'Vanjski saradnici'],
            ['seniorAssistantInternal', 'Stalno zaposleni'], ['seniorAssistantExternal', 'Vanjski saradnici'],
            ['assistantInternal', 'Stalno zaposleni'], ['assistantExternal', 'Vanjski saradnici']
        ],
        groups: [
            ['Akademska godina', 1, 3], ['Ukupno', 1, 3], ['Naučno / umjetničko / istraživačko zvanje', 12, 1]
        ],
        subgroups: [
            ['Redovni profesor / Naučni savjetnik', 2], ['Vanredni profesor / Viši naučni saradnik', 2],
            ['Docent / Naučni saradnik', 2], ['Viši asistent sa doktoratom / Stručni savjetnik', 2],
            ['Viši asistent / Viši stručni saradnik', 2], ['Asistent / Stručni saradnik', 2]
        ],
        leafHeaders: [
            ['fullProfessorInternal', 'Stalno zaposleni'], ['fullProfessorExternal', 'Vanjski saradnici'],
            ['associateProfessorInternal', 'Stalno zaposleni'], ['associateProfessorExternal', 'Vanjski saradnici'],
            ['assistantProfessorInternal', 'Stalno zaposleni'], ['assistantProfessorExternal', 'Vanjski saradnici'],
            ['seniorAssistantPhdInternal', 'Stalno zaposleni'], ['seniorAssistantPhdExternal', 'Vanjski saradnici'],
            ['seniorAssistantInternal', 'Stalno zaposleni'], ['seniorAssistantExternal', 'Vanjski saradnici'],
            ['assistantInternal', 'Stalno zaposleni'], ['assistantExternal', 'Vanjski saradnici']
        ]
    },
    'academic-staff-by-gender-and-age': {
        title: 'Pregled akademskog stalno zaposlenog osoblja prema spolu i godinama starosti',
        columns: [
            ['studyCycle', 'Ciklus studija'],
            ['studyProgram', 'Studijski program'],
            ['academicYear', 'Akademska godina'], ['academicTitle', 'Zvanje'], ['total', 'Ukupno'],
            ['male', 'Muški'], ['female', 'Ženski'], ['under25', '< 25'], ['age25To29', '25–29'],
            ['age30To34', '30–34'], ['age35To39', '35–39'], ['age40To44', '40–44'],
            ['age45To49', '45–49'], ['age50To54', '50–54'], ['age55To59', '55–59'],
            ['age60To64', '60–64'], ['age65Plus', '> 65']
        ],
        groups: [['Ciklus studija', 1, 2], ['Studijski program', 1, 2], ['Akademska godina', 1, 2], ['Zvanje', 1, 2], ['Ukupno', 1, 2], ['Spol', 2, 1], ['Godine starosti', 10, 1]],
        leafHeaders: [
            ['male', 'Muški'], ['female', 'Ženski'], ['under25', '< 25'], ['age25To29', '25–29'],
            ['age30To34', '30–34'], ['age35To39', '35–39'], ['age40To44', '40–44'],
            ['age45To49', '45–49'], ['age50To54', '50–54'], ['age55To59', '55–59'],
            ['age60To64', '60–64'], ['age65Plus', '> 65']
        ],
        additionalFilters: [['academicTitle', 'Zvanje']]
    },
    'academic-staff-student-ratio': {
        title: 'Pregled omjera broja akademskog osoblja i studenata',
        columns: [
            ['studyCycle', 'Ciklus studija'], ['studyProgram', 'Studijski program'], ['academicYear', 'Akademska godina'],
            ['totalTeachers', 'Ukupno'], ['internalTeachers', 'Stalno zaposleni'], ['externalTeachers', 'Vanjski saradnici'],
            ['totalStudents', 'Ukupno studenata'], ['studentInternalRatio', 'Omjer student / stalno zaposleni nastavnici'],
            ['internalExternalRatio', 'Omjer stalno zaposleni / vanjski nastavnici']
        ],
        groups: [['Ciklus studija', 1, 2], ['Studijski program', 1, 2], ['Akademska godina', 1, 2], ['Ukupno', 1, 2], ['Status nastavnika', 2, 1], ['Ukupno studenata', 1, 2], ['Omjer student / stalno zaposleni nastavnici', 1, 2], ['Omjer stalno zaposleni / vanjski nastavnici', 1, 2]],
        leafHeaders: [['internalTeachers', 'Stalno zaposleni'], ['externalTeachers', 'Vanjski saradnici']]
    },
    'student-mobility': {
        title: 'Pregled mobilnosti studenata u izvještajnom razdoblju',
        columns: [
            ['academicYear', 'Akademska godina'], ['mobilityDirection', 'Mobilnost (dolazna / odlazna)'],
            ['semester', 'Semestar'], ['mobilityProgram', 'Program mobilnosti'], ['studentName', 'Ime i prezime'],
            ['studyYear', 'Godina studija'], ['foreignInstitution', 'Inostrana institucija'], ['country', 'Država']
        ],
        additionalFilters: [['mobilityDirection', 'Mobilnost'], ['semester', 'Semestar'], ['mobilityProgram', 'Program mobilnosti']]
    },
    'students-by-gender': {
        title: 'Struktura studenata prema spolu u izvještajnom razdoblju',
        columns: [['studyCycle', 'Ciklus studija'], ['studyProgram', 'Studijski program'], ['academicYear', 'Akademska godina'], ['male', 'Muški spol'], ['female', 'Ženski spol'], ['total', 'Ukupno']],
        groups: [['Ciklus studija', 1, 2], ['Studijski program', 1, 2], ['Akademska godina', 1, 2], ['Upisani studenti', 3, 1]],
        leafHeaders: [['male', 'Muški spol'], ['female', 'Ženski spol'], ['total', 'Ukupno']]
    },
    'graduated-students': {
        title: 'Struktura završenih studenata u izvještajnom razdoblju',
        columns: [
            ['studyCycle', 'Ciklus studija'], ['studyProgram', 'Studijski program'], ['academicYear', 'Akademska godina'],
            ['total', 'Ukupno'], ['onTime', 'U predviđenom roku'], ['oneYearRepeated', 'Sa ponavljanjem jedne godine'],
            ['averageStudyDuration', 'Prosječna dužina studiranja'], ['averageGrade', 'Prosječna ocjena'],
            ['male', 'Muški'], ['female', 'Ženski'], ['domestic', 'Domaći'], ['foreign', 'Strani']
        ],
        groups: [['Ciklus studija', 1, 2], ['Studijski program', 1, 2], ['Akademska godina', 1, 2], ['Okončan studij (broj studenata)', 3, 1], ['Prosječna dužina studiranja', 1, 2], ['Prosječna ocjena', 1, 2], ['Spol', 2, 1], ['Status', 2, 1]],
        leafHeaders: [
            ['total', 'Ukupno'], ['onTime', 'U predviđenom roku'], ['oneYearRepeated', 'Sa ponavljanjem jedne godine'],
            ['male', 'Muški'], ['female', 'Ženski'], ['domestic', 'Domaći'], ['foreign', 'Strani']
        ]
    },
    'teaching-load-by-employee': {
        title: 'Nastavno opterećenje stalno zaposlenog akademskog osoblja',
        columns: [
            ['studyCycle', 'Ciklus studija'], ['studyProgram', 'Studijski program'], ['academicYear', 'Akademska godina'],
            ['employmentStatus', 'Status'], ['employeeName', 'Prezime i ime'], ['academicTitle', 'Zvanje'],
            ['teachingLoad', 'Nastavno opterećenje na studijskom programu'], ['loadShare', 'Udio opterećenja nastavnika u ukupnom opterećenju na SP']
        ],
        additionalFilters: [['employmentStatus', 'Status'], ['academicTitle', 'Zvanje']]
    },
    'teaching-load-by-type-and-status': {
        title: 'Pregled opterećenja po tipu nastave i statusu akademskog osoblja',
        columns: [
            ['studyCycle', 'Ciklus studija'], ['studyProgram', 'Studijski program'], ['academicYear', 'Akademska godina'],
            ['lecturesInternal', 'Stalno zaposleni'], ['lecturesExternal', 'Vanjski saradnici'],
            ['exercisesInternal', 'Stalno zaposleni'], ['exercisesExternal', 'Vanjski saradnici'],
            ['totalInternal', 'Stalno zaposleni'], ['totalExternal', 'Vanjski saradnici'],
            ['mentorshipInternal', 'Stalno zaposleni'], ['mentorshipExternal', 'Vanjski saradnici']
        ],
        groups: [['Ciklus studija', 1, 2], ['Studijski program', 1, 2], ['Akademska godina', 1, 2], ['Predavanja', 2, 1], ['Vježbe', 2, 1], ['Ukupno', 2, 1], ['Mentorski rad', 2, 1]],
        leafHeaders: [
            ['lecturesInternal', 'Stalno zaposleni'], ['lecturesExternal', 'Vanjski saradnici'],
            ['exercisesInternal', 'Stalno zaposleni'], ['exercisesExternal', 'Vanjski saradnici'],
            ['totalInternal', 'Stalno zaposleni'], ['totalExternal', 'Vanjski saradnici'],
            ['mentorshipInternal', 'Stalno zaposleni'], ['mentorshipExternal', 'Vanjski saradnici']
        ]
    }
};

class AnalyticalReport extends React.Component {
    state = {
        academicYearId: null,
        organizationalUnitId: null,
        studyProgramBasicId: null,
        studyProgramBasics: [],
        academicTitles: [],
        data: [],
        filters: {},
        searchTerm: '',
        loading: false,
        requested: false,
        activePage: 1
    };

    componentDidMount() {
        axios.get('/academic-titles')
            .then(response => this.setState({
                academicTitles: response.data
                    .map(title => title.name)
                    .filter(Boolean)
                    .sort((first, second) => first.localeCompare(second))
            }))
            .catch(() => this.setState({ academicTitles: [] }));
    }

    componentDidUpdate(previousProps) {
        if (previousProps.reportCode !== this.props.reportCode) {
            this.setState({ academicYearId: null, organizationalUnitId: null, studyProgramBasicId: null, studyProgramBasics: [], data: [], filters: {}, searchTerm: '', requested: false, activePage: 1 });
        }
    }

    handleRequiredChange = (event, { name, value }) => {
        if (name === 'organizationalUnitId') {
            this.setState({ organizationalUnitId: value || null, studyProgramBasicId: null, studyProgramBasics: [], data: [], requested: false });
            if (value && studyProgramReports.includes(this.props.reportCode)) {
                axios.post(`/study-programs/basic/${value}`, {}).then(response => {
                    this.setState({ studyProgramBasics: response.data
                        .filter(item => !item.studyProgramBasic.archived)
                        .sort((first, second) => first.studyProgramBasic.name.localeCompare(second.studyProgramBasic.name))
                        .map(item => ({
                            key: item.studyProgramBasic.id,
                            value: item.studyProgramBasic.id,
                            text: item.studyProgramBasic.name,
                            description: (item.studyProgramBasic.studyCycles || []).map(cycle => cycle.displayName).join(', ')
                        })) });
                }).catch(() => this.setState({ studyProgramBasics: [] }));
            }
            return;
        }
        this.setState({ [name]: value || null, data: [], requested: false });
    };

    loadReport = () => {
        const organizationalUnitRequired = organizationalUnitReports.includes(this.props.reportCode);
        if (!this.state.academicYearId || (organizationalUnitRequired && !this.state.organizationalUnitId)) return;
        this.setState({ loading: true, requested: true, data: [], searchTerm: '', activePage: 1 });
        axios.get(`/analytical-reports/${this.props.reportCode}`, { params: {
            academicYearId: this.state.academicYearId,
            organizationalUnitId: this.state.organizationalUnitId,
            studyProgramBasicId: this.state.studyProgramBasicId
        } }).then(response => this.setState({ data: response.data, loading: false }))
            .catch(() => this.setState({ data: [], loading: false }));
    };

    getFilteredData = () => {
        const searchTerm = this.state.searchTerm.trim().toLocaleLowerCase();
        const searchableColumns = reportDefinitions[this.props.reportCode].columns.map(column => column[0]);
        return this.state.data
            .filter(row => Object.keys(this.state.filters)
                .every(key => !this.state.filters[key] || row[key] === this.state.filters[key]))
            .filter(row => !searchTerm || searchableColumns.some(key => row[key] !== null
                && row[key] !== undefined
                && String(row[key]).toLocaleLowerCase().includes(searchTerm)));
    };

    getAdditionalFilterOptions = (filterKey) => {
        let values = this.state.data.map(row => row[filterKey]).filter(Boolean);
        if (filterKey === 'employmentStatus') {
            values = values.concat(['Stalno zaposlen', 'Spoljni radnik']);
        } else if (filterKey === 'academicTitle') {
            values = values.concat(this.state.academicTitles, ['Bez zvanja']);
        }
        return [...new Set(values)]
            .sort((first, second) => first.localeCompare(second))
            .map(value => ({ key: value, value, text: value }));
    };

    renderHeader = definition => {
        if (!definition.groups) {
            return <Table.Row>{definition.columns.map(column => <Table.HeaderCell key={column[0]}>{column[1]}</Table.HeaderCell>)}</Table.Row>;
        }
        const headerRows = [
            <Table.Row key="groups">{definition.groups.map((group, index) => <Table.HeaderCell key={`${group[0]}-${index}`} colSpan={group[1]} rowSpan={group[2]} textAlign="center">{group[0]}</Table.HeaderCell>)}</Table.Row>
        ];
        if (definition.subgroups) {
            headerRows.push(<Table.Row key="subgroups">{definition.subgroups.map((group, index) => <Table.HeaderCell key={`${group[0]}-${index}`} colSpan={group[1]} textAlign="center">{group[0]}</Table.HeaderCell>)}</Table.Row>);
        }
        headerRows.push(<Table.Row key="leaves">{definition.leafHeaders.map(column => <Table.HeaderCell key={column[0]} textAlign="center">{column[1]}</Table.HeaderCell>)}</Table.Row>);
        return headerRows;
    };

    render() {
        const definition = reportDefinitions[this.props.reportCode];
        const organizationalUnitRequired = organizationalUnitReports.includes(this.props.reportCode);
        const studyProgramFilterAvailable = studyProgramReports.includes(this.props.reportCode);
        const additionalFilters = definition.additionalFilters || [];
        const hasAdditionalFilters = studyProgramFilterAvailable || additionalFilters.length > 0;
        const filteredData = this.getFilteredData();
        const pageSize = 50;
        const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
        const pageData = filteredData.slice((this.state.activePage - 1) * pageSize, this.state.activePage * pageSize);

        return <div>
            <Form>
                <Segment style={requiredFilterSectionStyle}>
                    <Header as="h4" color="blue">Obavezni parametri</Header>
                    <Form.Group widths="equal">
                        {organizationalUnitRequired && <Form.Field required><Form.Dropdown label="Organizaciona jedinica" placeholder="Odaberite organizacionu jedinicu" name="organizationalUnitId" value={this.state.organizationalUnitId} options={this.props.organizationalUnits} onChange={this.handleRequiredChange} search fluid selection /></Form.Field>}
                        <Form.Field required><Form.Dropdown label="Akademska godina" placeholder="Odaberite akademsku godinu" name="academicYearId" value={this.state.academicYearId} options={this.props.academicYears} onChange={this.handleRequiredChange} search fluid selection /></Form.Field>
                        {organizationalUnitRequired && <Form.Field />}
                        {!organizationalUnitRequired && <Form.Field />}
                        {!organizationalUnitRequired && <Form.Field />}
                    </Form.Group>
                </Segment>
                {hasAdditionalFilters && <Segment style={additionalFilterSectionStyle}>
                    <Header as="h4" color="blue">Dodatni filteri</Header>
                    <Form.Group widths="equal" style={filterFieldGridStyle}>
                        {studyProgramFilterAvailable && <Form.Field><Form.Dropdown clearable label="Studijski program" placeholder="Svi studijski programi" name="studyProgramBasicId" value={this.state.studyProgramBasicId} options={this.state.studyProgramBasics} onChange={this.handleRequiredChange} search fluid selection /></Form.Field>}
                        {additionalFilters.map(filter => <Form.Field key={filter[0]}><Form.Dropdown clearable label={filter[1]} placeholder={`Odaberite: ${filter[1].toLowerCase()}`} value={this.state.filters[filter[0]] || null} options={this.getAdditionalFilterOptions(filter[0])} onChange={(event, { value }) => this.setState({ filters: { ...this.state.filters, [filter[0]]: value || null }, activePage: 1 })} search fluid selection /></Form.Field>)}
                    </Form.Group>
                </Segment>}
                <Button color="blue" floated="right" disabled={!this.state.academicYearId || (organizationalUnitRequired && !this.state.organizationalUnitId) || this.state.loading} onClick={this.loadReport}>Prikaži izvještaj</Button>
            </Form>
            <br /><br /><br />
            {this.state.loading ? <Loader active inline="centered">Učitavanje podataka može potrajati, molimo da sačekate.</Loader> : this.state.data.length ? <div className="analytical-report-table-scroll">
                <Table celled compact structured>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan={definition.columns.length}>
                                <Grid columns={2} stackable verticalAlign="middle">
                                    <Grid.Column width={14}>
                                        <Form.Input
                                            fluid
                                            icon="search"
                                            iconPosition="left"
                                            placeholder="Pretraga"
                                            value={this.state.searchTerm}
                                            onChange={(event, { value }) => this.setState({ searchTerm: value, activePage: 1 })}
                                        />
                                    </Grid.Column>
                                    <Grid.Column width={2} textAlign="center">
                                        <ExcelFile
                                            filename={definition.title}
                                            element={<Popup
                                                trigger={<Button animated="vertical" color="green" basic fluid style={{ textAlign: 'center' }}>
                                                    <Button.Content hidden><Icon name="download" /></Button.Content>
                                                    <Button.Content visible>Excel</Button.Content>
                                                </Button>}
                                                content="Preuzmi izvještaj u Excel-u"
                                                hideOnScroll
                                                position="top center"
                                            />}
                                        >
                                            <ExcelSheet data={filteredData} name="Izvještaj">
                                                {definition.columns.map(column => <ExcelColumn key={column[0]} label={column[1]} value={column[0]} />)}
                                            </ExcelSheet>
                                        </ExcelFile>
                                    </Grid.Column>
                                </Grid>
                            </Table.HeaderCell>
                        </Table.Row>
                        {this.renderHeader(definition)}
                    </Table.Header>
                    <Table.Body>{pageData.map((row, index) => <Table.Row key={index}>{definition.columns.map(column => <Table.Cell key={column[0]}>{row[column[0]] === null || row[column[0]] === undefined ? '-' : row[column[0]]}</Table.Cell>)}</Table.Row>)}</Table.Body>
                </Table>
                {pageCount > 1 && <PaginationFront postsPerPage={pageSize} totalPosts={filteredData.length} paginate={(event, data) => this.setState({ activePage: data.activePage })} activePage={this.state.activePage} />}
                {!filteredData.length && <Segment>Nema podataka za unesenu pretragu.</Segment>}
            </div> : this.state.requested ? <Segment>Nema podataka za odabrane parametre.</Segment> : null}
        </div>;
    }
}

export default AnalyticalReport;



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/AnalyticalReport.js